import { cn } from "@/components/kit/cn";

/** The surface the mark sits on. The halo under the ring is painted in this
 *  colour so the ring floats clear of the disc — "never lose the gap". */
export type LogoGround = "bg" | "card" | "pitch";

const GROUND: Record<LogoGround, string> = {
  bg: "stroke-bg",
  card: "stroke-card",
  pitch: "stroke-pitch",
};

/** Optical sizing (brand book §02): the ring thickens as the mark shrinks.
 *  4.6 is the master (≥48px); 8.4 is the pre-compensated favicon weight. */
function ringWeight(size: number) {
  return size >= 48 ? { ring: 4.6, halo: 9.4 } : { ring: 8.4, halo: 16.4 };
}

/** The Eclipse — the locked brand mark. The outlined ring is the FunMaker's
 *  room; the solid disc is you, rising across it; the amber lens is where you
 *  overlap. Geometry is exports/svg/metafun-mark-cream.svg — four numbers, none
 *  of them a choice. Minimum size 18px. Colours come from the theme. */
export function EclipseMark({
  size = 24,
  ground = "bg",
  className,
}: {
  size?: number;
  ground?: LogoGround;
  className?: string;
}) {
  const px = Math.max(18, size);
  const w = ringWeight(px);
  return (
    <svg
      width={px}
      height={px}
      viewBox="-4 -4 108 108"
      fill="none"
      aria-hidden
      className={cn("shrink-0", className)}
    >
      <circle cx="37" cy="64" r="32" className="fill-ink" />
      <path d="M32.34 32.34A32 32 0 0 1 68.66 68.66 32 32 0 0 1 32.34 32.34Z" className="fill-gold" />
      <circle cx="64" cy="37" r="32" strokeWidth={w.halo} className={GROUND[ground]} />
      <circle cx="64" cy="37" r="32" strokeWidth={w.ring} className="stroke-ink" />
    </svg>
  );
}

/** The logo as the loading state (brand book §07): both circles go to outline
 *  and drift apart along the 45° rise. Replaces every spinner — nothing else in
 *  MetaFun is allowed to spin. Reduced motion holds it still. */
export function EclipseWaiting({
  size = 24,
  label = "Loading",
  tone = "ink",
  className,
}: {
  size?: number;
  label?: string;
  /** `current` strokes with the parent's text colour — e.g. inside an amber
   *  button, where cream rings would vanish. */
  tone?: "ink" | "current";
  className?: string;
}) {
  const px = Math.max(18, size);
  const w = ringWeight(px);
  return (
    <svg
      width={px}
      height={px}
      viewBox="-12 -12 124 124"
      fill="none"
      role="img"
      aria-label={label}
      className={cn("shrink-0", className)}
    >
      <circle cx="37" cy="64" r="32" strokeWidth={w.ring} className={cn(tone === "current" ? "stroke-current" : "stroke-ink", "mf-drift")} />
      <circle cx="64" cy="37" r="32" strokeWidth={w.ring} className={cn(tone === "current" ? "stroke-current" : "stroke-ink", "mf-drift-r")} />
    </svg>
  );
}

/** Kept for existing call sites — now just the Eclipse, no tile. */
export function LogoTile({ size = 40, className }: { size?: number; className?: string }) {
  return <EclipseMark size={Math.round(size * 0.9)} className={className} />;
}

/** The hand-spaced wordmark (brand book §02): Figtree 600, cream, overall
 *  −4.5%, the t–a hole closed to −7.5%, u–n opened to −2%. Never coloured,
 *  never italic — that's typed text, not the wordmark. */
export function Wordmark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <span
      className={cn("font-semibold leading-none text-ink", className)}
      style={{ fontSize: size, letterSpacing: "-0.045em" }}
      aria-label="MetaFun"
    >
      <span aria-hidden>
        Me<span style={{ letterSpacing: "-0.075em" }}>ta</span>F<span style={{ letterSpacing: "-0.02em" }}>un</span>
      </span>
    </span>
  );
}
